// summitService.js
// Handles bidirectional JSON read/write for the summit capture tabs
// Uses the existing MSAL token from acquireTokenSilent
// Resolves drive/folder from projectConfig.sharepoint when provided,
// falling back to TCC defaults. Mirrors the pattern in platformService.js.

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";
const TCC_DRIVE_ID = "b!lQhaegUp90W6doNIX0gCPf75Qk46uGpGghXwC-9N3hPGlFCOtv4NT4o7JmQUKRJ-";
const TCC_FOLDER_PATH = "TCC Summit Data";

const FILES = {
  s3: "ReadinessCapture.json",
  s4: "UseCaseCapture.json",
  s5: "CurrentStateCapture.json",
  s6: "DataPlatformCapture.json",
  s7: "PilotPlanningCapture.json",
  s8: "GovernanceCapture.json",
  s9: "StoryCapture.json",
  s10: "ExecutionCapture.json",
  s11: "Day1WrapCapture.json",
  s14: "SummitCloseCapture.json",
  s15_accel: "AccelerationCapture.json",
};

function resolveDriveId(projectConfig) {
  return (projectConfig && projectConfig.sharepoint && projectConfig.sharepoint.driveId)
    ? projectConfig.sharepoint.driveId
    : TCC_DRIVE_ID;
}

function resolveFolderPath(projectConfig) {
  return (projectConfig && projectConfig.sharepoint && projectConfig.sharepoint.summitDataFolder)
    ? projectConfig.sharepoint.summitDataFolder
    : TCC_FOLDER_PATH;
}

async function graphFetch(url, accessToken, options) {
  const res = await fetch(url, {
    ...options,
    headers: {
      Authorization: "Bearer " + accessToken,
      "Content-Type": "application/json",
      ...(options && options.headers),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(function () { return {}; });
    throw new Error("Graph API " + res.status + ": " + (err.error?.message || res.statusText));
  }
  if (res.status === 204) return null;
  return res.json().catch(function () { return null; });
}

// Read a JSON file from SharePoint - returns null if not found
export async function readSummitFile(key, accessToken, projectConfig) {
  try {
    const driveId = resolveDriveId(projectConfig);
    const folderPath = resolveFolderPath(projectConfig);
    const filePath = folderPath + "/" + FILES[key];
    const url = GRAPH_BASE + "/drives/" + driveId + "/root:/" + filePath + ":/content";
    const res = await fetch(url, {
      headers: { Authorization: "Bearer " + accessToken },
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error("Read failed: " + res.status);
    return await res.json();
  } catch (err) {
    if (err.message && err.message.includes("404")) return null;
    throw err;
  }
}

// Write a JSON file to SharePoint - creates file and folder if needed
export async function writeSummitFile(key, data, accessToken, projectConfig) {
  const driveId = resolveDriveId(projectConfig);
  const folderPath = resolveFolderPath(projectConfig);
  const filePath = folderPath + "/" + FILES[key];

  // Ensure folder exists first
  try {
    await ensureFolder(driveId, accessToken, folderPath);
  } catch (e) {
    // Folder may already exist - continue
  }

  const url = GRAPH_BASE + "/drives/" + driveId + "/root:/" + filePath + ":/content";
  const res = await fetch(url, {
    method: "PUT",
    headers: {
      Authorization: "Bearer " + accessToken,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(function () { return {}; });
    throw new Error("Write failed " + res.status + ": " + (err.error?.message || res.statusText));
  }
  return true;
}

// Ensure the summit data folder exists at the drive root
async function ensureFolder(driveId, accessToken, folderPath) {
  const url = GRAPH_BASE + "/drives/" + driveId + "/root/children";
  await graphFetch(url, accessToken, {
    method: "POST",
    body: JSON.stringify({
      name: folderPath,
      folder: {},
      "@microsoft.graph.conflictBehavior": "replace",
    }),
  });
}

// Hook-friendly wrapper: returns { ok, driveId }
export async function testSummitConnection(accessToken, projectConfig) {
  try {
    const driveId = resolveDriveId(projectConfig);
    await graphFetch(GRAPH_BASE + "/drives/" + driveId, accessToken);
    return { ok: true, driveId };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}
